"use client";

import { handleGenerateProductLinkProps } from "@faire/deprecated-retailer-visitor-shared/components/SearchProducts/utils";
import { ProductUrlParameters } from "@faire/deprecated-retailer-visitor-shared/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/ProductLink/useProductLinkProps";
import { ProductTileClickProviderProps } from "@faire/deprecated-retailer-visitor-shared/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/ProductTileClickProvider/search/ProductTileClickProviderProps";
import { useOnProductClickForSearchPage } from "@faire/deprecated-retailer-visitor-shared/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/productTileActions/useOnProductClickForSearchPage";
import { useSwatchInteraction } from "@faire/deprecated-retailer-visitor-shared/features/MarketplaceItems/ProductTile/__internal__/components/Swatch/SwatchInteractionContextProvider";
import { getProductUrlWithSwatchOption } from "@faire/deprecated-retailer-visitor-shared/features/MarketplaceItems/ProductTile/__internal__/components/utils/productUrlUtils";
import { usePrefetchOnTouch } from "@faire/deprecated-retailer-visitor-shared/hooks/pdpPrefetching/usePDPPrefetchOnTouch";
import { getUser } from "@faire/deprecated-retailer-visitor-shared/serialized-data/getUser";
import { usePreloadNavigationToPDP } from "@faire/deprecated-retailer-visitor-shared/views/product/preload/preloadNavigationToPDP";
// eslint-disable-next-line @faire/enforce-internal-folder-encapsulation
import { useSearchPageFilterKeys } from "@faire/deprecated-retailer-visitor-shared/views/search-v2/__internal__/LazySearchPage/__internal__/SearchPage/__internal__/SearchPageHeaderAndResults/__internal__/SearchPageResults/__internal__/SearchPageProductResults/__internal__/useSearchPageFilterKeys";
import { useSearchPageProductSearchString } from "@faire/deprecated-retailer-visitor-shared/views/search-v2/SearchPageQueryParams/useSearchPageProductSearchString";
import { Link } from "@faire/web/ui/LinkAnchor";
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation } from "react-router-dom";

/**
 * A client component provides very basic functionality to redirect to pdp.
 */
export const SearchProductLink = ({
  data: {
    productToken,
    impressionId,
    images,
    isPromoted,
    promotedBy,
    badgeList,
    brandToken,
    selectedProductOptionToken,
    bestImage,
  },
  children,
}: {
  data: ProductTileClickProviderProps;
  children: ReactNode;
}) => {
  const { selectedSwatchOptionToken } = useSwatchInteraction();
  const { onProductClick } = useOnProductClickForSearchPage({
    token: productToken,
    isPromoted,
    impressionId,
    promotedBy,
    badgeList,
    bestImage,
    images,
    brandToken,
  });

  const preloadNavigationToPDP = usePreloadNavigationToPDP();

  const productLinkProps = useGenerateProductLinkPropsFn({
    token: productToken,
    brandToken,
    impressionId,
    selectedProductOptionToken,
  });
  const to = productLinkProps?.productUrl ?? "";
  const [productUrl, setProductUrl] = useState<string>(to);

  const getProductUrl = useCallback(
    (selectedToken?: string) => {
      return getProductUrlWithSwatchOption(
        productLinkProps?.productUrl ?? "",
        selectedToken
      );
    },
    [productLinkProps?.productUrl]
  );

  useEffect(() => {
    if (selectedSwatchOptionToken) {
      setProductUrl(getProductUrl(selectedSwatchOptionToken));
    }
  }, [getProductUrl, selectedSwatchOptionToken]);

  const onMouseDown = () => {
    preloadNavigationToPDP(
      {
        productToken,
        brandToken,
        bestImage,
        images: images ?? [],
      },
      productUrl
    );
  };

  const { onTouchStart } = usePrefetchOnTouch(
    {
      productToken,
      bestImage,
      images: images ?? [],
      brandToken,
    },
    productUrl
  );

  return (
    <div tabIndex={0}>
      <Link
        onClick={onProductClick}
        onMouseDown={onMouseDown}
        onTouchStart={onTouchStart}
        data-test-id="product-tile-link"
        to={productUrl}
        replace={productLinkProps?.replace}
      >
        {children}
      </Link>
    </div>
  );
};

export const useGenerateProductLinkPropsFn = (params: ProductUrlParameters) => {
  const productSearchString = useSearchPageProductSearchString();
  const filterKeys = useSearchPageFilterKeys();
  const location = useLocation();
  const user = getUser();
  const getLinkPropsFn = handleGenerateProductLinkProps(
    productSearchString,
    params,
    // TODO: it looks like this field isn't used, we only pass refF to the backend on the brand page
    filterKeys || [],
    location,
    user
  );
  const linkProps = useMemo(() => getLinkPropsFn(), [getLinkPropsFn]);
  return linkProps;
};
